import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    ButtonColor,
    ButtonType,
    canUseIntersectionObserver,
    Image,
    PauseIcon,
    PlayIcon,
    PureButton,
    useIntersection,
} from '@jsmdg/yoshi';
import { type BoxGifSliderGif } from '../../../../shared/types/boxGifSlider';
import { type FragmentContext } from '../../../../shared/types/fragmentContext';
import { useGifPlayback } from '../../../hooks/useGifPlayback';
import styles from './BoxGifWrapper.module.scss';

type BoxGifWrapperProps = {
    readonly title: string;
    readonly description: string;
    readonly gif: BoxGifSliderGif;
    readonly lazyLoad: boolean;
};

const messages = defineMessages({
    a11yPauseGif: { defaultMessage: 'Animation pausieren' },
    a11yPlayGif: { defaultMessage: 'Animation abspielen' },
});

const BoxGifWrapper = ({ description, gif, lazyLoad, title }: BoxGifWrapperProps): JSX.Element => {
    const intl = useIntl();
    const { isMydays } = useFragmentContext<FragmentContext>();
    const { imageContainerRef, isPlaying, pausedSrc, togglePlayback } = useGifPlayback();

    const [isNearViewport, setTargetElement] = useIntersection({
        rootMargin: '200px',
        triggerOnce: true,
    });
    const [hasNoObserver, setHasNoObserver] = useState(false);
    const showGif = isNearViewport || hasNoObserver;

    useEffect(() => {
        const node = imageContainerRef.current;
        if (!node) return;

        if (canUseIntersectionObserver()) {
            setTargetElement(node);
        } else {
            setHasNoObserver(true);
        }
    }, [imageContainerRef, setTargetElement]);

    const a11yLabel = isPlaying
        ? intl.formatMessage(messages.a11yPauseGif)
        : intl.formatMessage(messages.a11yPlayGif);

    return (
        <div
            className={classNames(styles.wrapper, 'd-flex flex-column align-items-start h-100', {
                'align-items-sm-center ': isMydays,
            })}
        >
            <div
                ref={imageContainerRef}
                className={classNames(
                    'position-relative d-flex overflow-hidden mb-1-5x',
                    styles.imageWrapper,
                )}
            >
                {showGif ? (
                    <Image
                        className={classNames(styles.gif, 'w-100')}
                        src={!isPlaying && pausedSrc ? pausedSrc : gif.src}
                        alt={gif.alt}
                        lazyLoad={lazyLoad}
                        srcSet={isPlaying ? gif.srcset : undefined}
                        sizes={isPlaying ? gif.sizes : undefined}
                    />
                ) : (
                    <div className={styles.gif} data-testid="box-gif-placeholder" />
                )}
                <PureButton
                    type={ButtonType.Button}
                    color={ButtonColor.White}
                    className={classNames(
                        styles.controlButton,
                        'position-absolute d-flex align-items-center justify-content-center',
                        {
                            'rounded-circle': isMydays,
                        },
                    )}
                    aria-label={a11yLabel}
                    onClick={togglePlayback}
                    aria-pressed={!isPlaying}
                >
                    {isPlaying ? <PauseIcon size={16} /> : <PlayIcon size={16} />}
                </PureButton>
            </div>
            <p
                className={classNames(styles.title, 'theme-text-subtitle-text mb-1x', {
                    'p-0 px-sm-1x': isMydays,
                })}
            >
                {title}
            </p>
            <p className={styles.description}>{description}</p>
        </div>
    );
};

export { BoxGifWrapper };
